import React, { Suspense, lazy } from "react";
import "./App.css";
import "./admin.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Services from "./components/Services";
import About from "./components/About";
import Team from "./components/Team";
import FeesPreview from "./components/FeesPreview";
import Process from "./components/Process";
import Testimonials from "./components/Testimonials";
import FAQ from "./components/FAQ";
import Insurance from "./components/Insurance";
import NewsletterCapture from "./components/NewsletterCapture";
import Contact from "./components/Contact";
import Location from "./components/Location";
import Footer from "./components/Footer";
import WhatsAppFAB from "./components/WhatsAppFAB";
import KnowledgeBaseTab from "./components/KnowledgeBaseTab";
import StickyMobileBookBar from "./components/StickyMobileBookBar";
import SitePageRoute from "./components/SitePageRoute";
import FaviconSwitcher from "./components/FaviconSwitcher";
import { boroughs } from "./data/boroughs";
import ProtectedRoute from "./components/ProtectedRoute";
import SEO from "./components/SEO";
import BookingModal from "./components/BookingModal";
import SiteSearch from "./components/SiteSearch";
import ReviewsCarousel from "./components/ReviewsCarousel";
import ColleaguesCarousel from "./components/ColleaguesCarousel";
import KrinaGallery from "./components/KrinaGallery";
import ProofStrip from "./components/ProofStrip";
import "./components/reviews-marquee.css";
import { homePageSEO } from "./data/seo";
import {
  getMedicalClinicLD,
  getPersonKrinaLD,
  getPhysicianLD,
  getOrganizationLD,
  getWebSiteLD,
  getSpeakableLD,
} from "./data/structuredData";
import { AuthProvider } from "./context/AuthContext";
import { BookingProvider } from "./context/BookingContext";
import { SearchProvider } from "./context/SearchContext";
import { ConsentProvider } from "./context/ConsentContext";
import { BrandThemeProvider } from "./context/BrandThemeContext";
import { PracticeProvider, usePractice } from "./context/PracticeContext";
import { ConfirmDialogProvider } from "./components/ui/confirm-dialog";
import CookieConsent from "./components/CookieConsent";
import useGA4 from "./hooks/useGA4";

// Admin screens are code-split so patients never download the dashboard bundle
// (recharts, dnd-kit, TipTap, ~18k lines). Each loads on first visit to its route.
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const AdminSettings = lazy(() => import("./components/AdminSettings"));
const AdminPages = lazy(() => import("./components/AdminPages"));
const AdminNewsletter = lazy(() => import("./components/AdminNewsletter"));
const AdminRedeem = lazy(() => import("./components/AdminRedeem"));
const AdminShop = lazy(() => import("./components/AdminShop"));
const AdminAesthetics = lazy(() => import("./components/AdminAesthetics"));
const AdminLocalRank = lazy(() => import("./components/AdminLocalRank"));
const AdminPlaybooks = lazy(() => import("./components/AdminPlaybooks"));
const AdminReviewRequests = lazy(() => import("./components/AdminReviewRequests"));
const AdminSourceTracking = lazy(() => import("./components/AdminSourceTracking"));
const AdminConversions = lazy(() => import("./components/AdminConversions"));
const AdminCQCLogs = lazy(() => import("./components/AdminCQCLogs"));
const AdminColleagues = lazy(() => import("./components/AdminColleagues"));
const AdminLog = lazy(() => import("./components/AdminLog"));
const AdminPracticeDetails = lazy(() => import("./components/AdminPracticeDetails"));

// Paid-ad landing pages — nav-free, code-split (ad traffic only).
const AdsLanding = lazy(() => import("./components/landing/AdsLanding"));
const MassageLanding = lazy(() => import("./components/landing/MassageLanding"));
const ClinicalLanding = lazy(() => import("./components/landing/ClinicalLanding"));

// Public page-level routes — code-split so they don't bloat the homepage
// bundle. Each loads on first navigation to its route (wrapped by the
// existing <Suspense fallback={<RouteFallback/>}> around <Routes>).
const ServicePage = lazy(() => import("./components/ServicePage"));
const ReferralPage = lazy(() => import("./components/ReferralPage"));
const VideosLibrary = lazy(() => import("./components/VideosLibrary"));
const WellnessMassage = lazy(() => import("./components/WellnessMassage"));
const Media = lazy(() => import("./components/Media"));
const Courses = lazy(() => import("./components/Courses"));
const Partners = lazy(() => import("./components/Partners"));
const KnowledgeBase = lazy(() => import("./components/KnowledgeBase"));
const KnowledgeBaseArticle = lazy(() => import("./components/KnowledgeBaseArticle"));
const LinkInBio = lazy(() => import("./components/LinkInBio"));
const Fees = lazy(() => import("./components/Fees"));
const FAQPage = lazy(() => import("./components/FAQPage"));
const ThankYou = lazy(() => import("./components/ThankYou"));
const BoroughPage = lazy(() => import("./components/BoroughPage"));
const LegalPage = lazy(() => import("./components/LegalPage"));
const StandardPage = lazy(() => import("./components/StandardPage"));
const TechnologyPage = lazy(() => import("./components/TechnologyPage"));
const AreasHub = lazy(() => import("./components/AreasHub"));
const EvidenceLibraryPage = lazy(() => import("./components/EvidenceLibraryPage"));
const SearchablePreview = lazy(() => import("./components/SearchablePreview"));
const AdminLogin = lazy(() => import("./components/AdminLogin"));
const EmergencyReset = lazy(() => import("./components/EmergencyReset"));

function RouteFallback() {
  return (
    <div className="admin-loading" data-testid="admin-route-loading" style={{ color: "#0F766E" }}>
      <Loader2 className="admin-spinner" size={32} />
      <span>Loading…</span>
    </div>
  );
}

function GlobalAnalytics() {
  useGA4();
  return null;
}

// SPA route changes start at the top of the new page; hash links scroll to their target once it renders.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el || ++tries > 30) {
        clearInterval(timer);
        if (el) el.scrollIntoView({ block: 'start' });
      }
    }, 50);
    return () => clearInterval(timer);
  }, [pathname, hash]);
  return null;
}

function HomePage() {
  const practice = usePractice();
  const homeStructuredData = [
    getOrganizationLD(),
    getWebSiteLD(), // now includes SearchAction — sitelinks search box eligible
    getMedicalClinicLD(practice),
    getPhysicianLD(practice),   // NEW — Krina's clinical practice as a Physician entity
    getPersonKrinaLD(),
    // Voice-assistant readback: hero headline + hero subtitle marked as speakable.
    getSpeakableLD({
      cssSelector: [".hero-title", ".hero-description"],
    }),
  ];
  return (
    <>
      <SEO
        title={homePageSEO.title}
        description={homePageSEO.description}
        path={homePageSEO.path}
        image={homePageSEO.image}
        structuredData={homeStructuredData}
      />
      <Hero />
      <ReviewsCarousel />
      <ProofStrip />
      <KrinaGallery />
      <Services />
      <FeesPreview />
      <About />
      <Team />
      <Process />
      <Testimonials />
      <ColleaguesCarousel />
      <FAQ />
      <NewsletterCapture />
      <Insurance />
      <Contact />
      <Location />
    </>
  );
}

function PublicChrome({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isLandingRoute = location.pathname.startsWith("/lp/");

  if (isAdminRoute) {
    return <main className="admin-shell">{children}</main>;
  }
  // Paid-ad landing pages render fully bare (no header/footer/FAB/sticky bar)
  // so ad traffic has one distraction-free path to the booking CTA.
  if (isLandingRoute) {
    return <>{children}</>;
  }

  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
      <WhatsAppFAB />
      <KnowledgeBaseTab />
      <StickyMobileBookBar />
    </>
  );
}

// Preserves the article slug when redirecting from the old /knowledge-base/:slug
// URL to the new /tmd-knowledge-base/:slug URL. Google will still see this as a
// standard client-side redirect (equivalent to a 301 in terms of SEO signal).
function LegacyArticleRedirect() {
  const { slug } = useParams();
  const destination = `/tmd-knowledge-base/${slug}`;
  return <LegacyRedirect to={destination} />;
}

// Wraps <Navigate> for legacy Squarespace URLs so we can build a live
// "top legacy paths" report from tracking_clicks — every hit fires a
// single POST /api/tracking/legacy-hit before the browser navigates so
// Krina can spot which old URLs still drive traffic + prioritise
// rewrites / robots.txt Disallow additions.
function LegacyRedirect({ to }) {
  const location = useLocation();
  React.useEffect(() => {
    const path = location.pathname + (location.search || '');
    const body = JSON.stringify({ path, destination: to });
    // Fire-and-forget beacon; sendBeacon works even during unload/navigate.
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/tracking/legacy-hit`;
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
      }
    } catch (_) { /* no-op */ }
    // Intentionally empty deps — we only want to log the initial hit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Navigate to={to} replace />;
}

function App() {
  return (
    <BrowserRouter>
        <BrandThemeProvider>
          <PracticeProvider>
          <ConsentProvider>
            <AuthProvider>
              <BookingProvider>
                <SearchProvider>
                <ConfirmDialogProvider>
                <div className="App" id="home">
                  <PublicChrome>
                  <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/services/:serviceId" element={<ServicePage />} />
                    <Route path="/videos" element={<VideosLibrary />} />
                    <Route path="/wellness-massage" element={<WellnessMassage />} />
                    <Route path="/media" element={<Media />} />
                    <Route path="/courses" element={<Courses />} />
                    <Route path="/partners" element={<Partners />} />
                    <Route path="/knowledge-base" element={<LegacyRedirect to="/tmd-knowledge-base" />} />
                    <Route path="/knowledge-base/:slug" element={<LegacyArticleRedirect />} />
                    <Route path="/tmd-knowledge-base" element={<KnowledgeBase />} />
                    <Route path="/tmd-knowledge-base/:slug" element={<KnowledgeBaseArticle />} />
                    <Route path="/link-in-bio" element={<LinkInBio />} />
                    <Route path="/lp/jaw-clinic" element={<AdsLanding />} />
                    <Route path="/lp/jaw-massage" element={<MassageLanding />} />
                    <Route path="/lp/headache-migraine" element={<ClinicalLanding configKey="headaches" />} />
                    <Route path="/lp/tinnitus" element={<ClinicalLanding configKey="tinnitus" />} />
                    <Route path="/lp/facial-pain" element={<ClinicalLanding configKey="facialPain" />} />
                    <Route path="/fees" element={<Fees />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/refer" element={<ReferralPage />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                    {boroughs.map((b) => (
                      <Route
                        key={b.slug}
                        path={`/tmd-physio-${b.slug}`}
                        element={<BoroughPage />}
                      />
                    ))}
                    <Route path="/the-tmd-physio-standard" element={<StandardPage />} />
                    <Route path="/technology-and-treatments" element={<TechnologyPage />} />
                    <Route path="/areas-we-serve" element={<AreasHub />} />
                    <Route path="/evidence-library" element={<EvidenceLibraryPage />} />
                    <Route path="/preview/searchable/:id" element={<SearchablePreview />} />
                    <Route path="/legal/:slug" element={<LegalPage />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/emergency-reset" element={<EmergencyReset />} />
                    <Route
                      path="/admin/dashboard"
                      element={
                        <ProtectedRoute>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings"
                      element={
                        <ProtectedRoute>
                          <AdminSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/pages"
                      element={
                        <ProtectedRoute>
                          <AdminPages />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/newsletter"
                      element={
                        <ProtectedRoute>
                          <AdminNewsletter />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/content"
                      element={
                        <ProtectedRoute>
                          <AdminNewsletter />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/redeem"
                      element={
                        <ProtectedRoute>
                          <AdminRedeem />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/shop"
                      element={
                        <ProtectedRoute>
                          <AdminShop />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/aesthetics"
                      element={
                        <ProtectedRoute>
                          <AdminAesthetics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/local-rank"
                      element={
                        <ProtectedRoute>
                          <AdminLocalRank />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/playbooks"
                      element={
                        <ProtectedRoute>
                          <AdminPlaybooks />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/review-requests"
                      element={
                        <ProtectedRoute>
                          <AdminReviewRequests />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/sources"
                      element={
                        <ProtectedRoute>
                          <AdminSourceTracking />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/conversions"
                      element={
                        <ProtectedRoute>
                          <AdminConversions />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/cqc"
                      element={
                        <ProtectedRoute>
                          <AdminCQCLogs />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/colleagues"
                      element={
                        <ProtectedRoute>
                          <AdminColleagues />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/log"
                      element={
                        <ProtectedRoute>
                          <AdminLog />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/practice-details"
                      element={
                        <ProtectedRoute>
                          <AdminPracticeDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    {/* Legacy Squarespace URL redirects — Google Search
                        Console flagged ~30 old URLs from Krina's previous
                        Squarespace site (2026-08-28). Map each to the
                        closest new page so SEO link equity is preserved and
                        old bookmarks/email links land on something useful,
                        instead of the catch-all bouncing to /. Every hit is
                        logged via POST /api/tracking/legacy-hit so the
                        admin dashboard can spot which of these still drive
                        traffic (see Phase 90). */}
                    <Route path="/tmjdphysiotherapy" element={<LegacyRedirect to="/services/tmd-treatment" />} />
                    <Route path="/tmj-physiotherapy" element={<LegacyRedirect to="/services/tmd-treatment" />} />
                    <Route path="/tmd" element={<LegacyRedirect to="/services/tmd-treatment" />} />
                    <Route path="/tmj" element={<LegacyRedirect to="/services/tmd-treatment" />} />
                    <Route path="/tinnitus" element={<LegacyRedirect to="/services/tinnitus-management" />} />
                    <Route path="/tinnitus-treatment" element={<LegacyRedirect to="/services/tinnitus-management" />} />
                    <Route path="/headaches" element={<LegacyRedirect to="/services/headache-migraine-relief" />} />
                    <Route path="/migraine" element={<LegacyRedirect to="/services/headache-migraine-relief" />} />
                    <Route path="/migraines" element={<LegacyRedirect to="/services/headache-migraine-relief" />} />
                    <Route path="/low-level-laser-therapy" element={<LegacyRedirect to="/services/tmd-treatment" />} />
                    <Route path="/laser-therapy" element={<LegacyRedirect to="/services/tmd-treatment" />} />
                    <Route path="/why-do-i-have-tmd-the-online-self-help-programme" element={<LegacyRedirect to="/tmd-knowledge-base" />} />
                    <Route path="/online-self-help-programme" element={<LegacyRedirect to="/tmd-knowledge-base" />} />
                    <Route path="/self-help" element={<LegacyRedirect to="/tmd-knowledge-base" />} />
                    <Route path="/mystory" element={<LegacyRedirect to="/#about" />} />
                    <Route path="/my-story" element={<LegacyRedirect to="/#about" />} />
                    <Route path="/about" element={<LegacyRedirect to="/#about" />} />
                    <Route path="/book-now" element={<LegacyRedirect to="/#book" />} />
                    <Route path="/booking" element={<LegacyRedirect to="/#book" />} />
                    <Route path="/contact" element={<LegacyRedirect to="/#contact" />} />
                    <Route path="/new-page-1" element={<LegacyRedirect to="/" />} />

                    {/* Global 404 catch-all — genuinely unknown URLs (typos,
                        dead links, invalid borough slugs like /tmd-physio-mars)
                        render a proper Not Found page carrying `noindex`, so
                        Google drops them cleanly instead of logging soft-404s
                        or redirect noise. Real legacy paths still redirect via
                        their own routes above. Still logged for orphan-link
                        spotting. */}
                    <Route path="*" element={<SitePageRoute />} />
                  </Routes>
                  </Suspense>
                </PublicChrome>
                <BookingModal />
                <SiteSearch />
                <CookieConsent />
                <GlobalAnalytics />
                <ScrollToTop />
                <FaviconSwitcher />
                <Toaster position="top-right" richColors />
              </div>
              </ConfirmDialogProvider>
              </SearchProvider>
              </BookingProvider>
            </AuthProvider>
          </ConsentProvider>
          </PracticeProvider>
        </BrandThemeProvider>
      </BrowserRouter>
  );
}

export default App;
